import React from 'react';
import { FormText, Input, InputGroup, InputGroupText, InputProps, Label } from 'reactstrap';
import { getIn, ErrorMessage } from 'formik';
import './input.css';

export interface InputFieldProps {
  /** Injected by `<Field component={InputField} />`, or passed explicitly from a render prop */
  field: { name: string; value?: any; onChange?: any; onBlur?: any };
  form: { touched: any; errors: any };

  label?: React.ReactNode;
  type?: InputProps['type'];
  placeholder?: string;
  min?: number | string;
  max?: number | string;
  /** Note: `disable`, not `disabled` */
  disable?: boolean;
  valid?: boolean;
  step?: number | string;
  id?: string;
  autoFocus?: boolean;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  subText?: React.ReactNode;
  className?: string;
  /** Receives `(value, error)` rather than the event - this replaces Formik's own `field.onBlur` */
  onBlur?: (value: any, error?: any) => void;

  /** Anything else is forwarded straight to reactstrap's `<Input>` */
  [key: string]: any;
}

const InputField = ({
  field,
  form: { touched, errors },
  label,
  type,
  placeholder,
  min,
  max,
  disable,
  valid,
  step,
  id,
  autoFocus,
  suffix,
  prefix,
  subText,
  ...props
}: InputFieldProps) => {
  const InputWrapper: React.ElementType = suffix || prefix ? InputGroup : React.Fragment;

  return (
    <>
      {label ? (
        <Label htmlFor={id || field.name} className='pe-2'>
          {label}
        </Label>
      ) : null}

      <InputWrapper>
        {prefix ? <InputGroupText>{prefix}</InputGroupText> : null}

        <Input
          id={id || field.name}
          step={step}
          disabled={disable}
          bsSize='sm'
          type={type}
          placeholder={placeholder}
          min={min}
          max={max}
          {...field}
          {...props}
          valid={touched[field.name] && !errors[field.name] ? valid : undefined}
          invalid={
            (touched[field.name] && !!errors[field.name]) ||
            (getIn(errors, field.name) && getIn(touched, field.name))
          }
          onWheelCapture={(e: React.WheelEvent) => {
            (e.target as HTMLElement).blur();
          }}
          onBlur={() => {
            props.onBlur && props.onBlur(field.value, errors[field.name]); // Callback functions that accept only the value of the input
          }}
          autoComplete='off'
          className={props.className}
          autoFocus={autoFocus}
        />

        {suffix ? <InputGroupText>{suffix}</InputGroupText> : null}
      </InputWrapper>

      {(touched[field.name] && errors[field.name] && (
        <ErrorMessage component='div' name={field.name} className='input-feedback' />
      )) ||
        (getIn(errors, field.name) && getIn(touched, field.name) && (
          <ErrorMessage component='div' name={field.name} className='input-feedback' />
        ))}

      {Boolean(subText) && <FormText className='text-sm'>{subText}</FormText>}
    </>
  );
};

export default InputField;
